import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import {
	createPersonBankAccountRequest,
	updatePersonBankAccountRequest,
	validatePersonBankAccountRequest,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/apis/useBankAccounts.ts';
import type { BankAccountVerifyFn } from '@dealstack/frontend/shared/components/BankAccountForm/useBankAccountForm.types.ts';
import type {
	BankAccountDetails,
	BankAccountFormValues,
} from '@dealstack/frontend/shared/components/BankAccountForm/types.ts';
import { toBankAccountPayload } from '@dealstack/frontend/shared/components/BankAccountForm/BankAccountForm.utils.ts';

interface Args {
	/** Person the account hangs off; every request the drawer makes is scoped to them. */
	personId: string;
	bankAccount?: BankAccountDetails | null;
	onClose: () => void;
	/** Run once the drawer closes, so the host can refresh what it renders. */
	onSaved: () => void;
}

export const useBankAccountDrawer = ({ personId, bankAccount, onClose, onSaved }: Args) => {
	// Draft shell created lazily on the first IBAN lookup; the final save updates it.
	const shellBankAccountIdRef = useRef<string | null>(null);

	const closeDrawer = () => {
		// The shell row stays — it carries this attempt's lookup log. Dropping the local
		// handle keeps a later "Add" from writing into it for a different entity; the
		// next create resolves the person's shell server-side.
		shellBankAccountIdRef.current = null;
		onSaved();
		onClose();
	};

	const createBankAccountMutation = useMutation({
		mutationFn: createPersonBankAccountRequest,
	});
	const updateBankAccountMutation = useMutation({
		mutationFn: updatePersonBankAccountRequest,
	});
	const isRequestLoading =
		createBankAccountMutation.isPending || updateBankAccountMutation.isPending;

	const verifyIdentifiers: BankAccountVerifyFn = async (dto, { countryCode }) => {
		let accountId = bankAccount?.id ?? shellBankAccountIdRef.current;
		if (!accountId) {
			const created = await createPersonBankAccountRequest({
				personId,
				body: { countryCode: countryCode ?? undefined },
			});
			accountId = created?.id ?? null;
			if (!accountId) {
				throw new Error('Failed to create the bank account');
			}
			shellBankAccountIdRef.current = accountId;
		}
		return await validatePersonBankAccountRequest({
			personId,
			bankAccountId: accountId,
			body: dto,
		});
	};

	const onFormSubmit = async (formValues: BankAccountFormValues, bankAccountId?: string) => {
		const body = toBankAccountPayload(formValues);

		if (!body) {
			return;
		}

		const effectiveId = bankAccountId ?? shellBankAccountIdRef.current;
		if (effectiveId) {
			updateBankAccountMutation.mutate(
				{
					personId,
					bankAccountId: effectiveId,
					body,
				},
				{
					onSuccess: () => {
						closeDrawer();
					},
				}
			);
		} else {
			createBankAccountMutation.mutate(
				{
					personId,
					body,
				},
				{
					onSuccess: () => {
						closeDrawer();
					},
				}
			);
		}
	};

	return {
		onFormSubmit,
		verifyIdentifiers,
		closeDrawer,
		isRequestLoading,
	};
};
